import type { RefObject } from 'react';
import { useEffect, useEffectEvent } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { useSearchParams } from 'react-router';

import type {
  ExtendedUIState,
  RouteState,
} from '#src/components/search/InstantSearch/types.tsx';
import { SEARCH_PARAMS } from '#src/components/search/InstantSearch/types.tsx';
import {
  routeToState,
  stateToRoute,
} from '#src/components/search/InstantSearch/utils.ts';
import { useConfigContext } from '#src/contexts/config/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useVirtualToggleManager } from '#src/hooks/useVirtualToggleManager.ts';

export const NavigationHandler = ({
  mainIndex,
  lastRouteState,
  initialUiState,
}: {
  mainIndex: string;
  lastRouteState: RefObject<RouteState>;
  initialUiState: ExtendedUIState;
}) => {
  const {
    algoliaIndexes,
    so5: { cardMaxGrade },
  } = useConfigContext();
  const { setIndexUiState } = useInstantSearch<ExtendedUIState>();
  const [searchParams] = useSearchParams();
  const setVirtualToggleFilters = useVirtualToggleManager();
  const { language } = useIntlContext();

  const onSearchParamsChange = useEffectEvent(
    (inputSearchParams: URLSearchParams) => {
      const algoliaParamsValues = Object.values(SEARCH_PARAMS) as string[];
      const filteredSearchParams = Object.fromEntries(
        Array.from(inputSearchParams).filter(([key]) =>
          algoliaParamsValues.includes(key)
        )
      ) as Record<SEARCH_PARAMS, string>;

      // Serialize + Deserialize filteredSearchParams to ensure a consistent order of properties
      const stateFromRoute = routeToState(
        filteredSearchParams,
        mainIndex,
        algoliaIndexes,
        language
      );
      const routeState = stateToRoute(
        stateFromRoute,
        mainIndex,
        algoliaIndexes,
        language,
        cardMaxGrade
      );
      const strLastRouteState = JSON.stringify(lastRouteState.current);
      const strCurrentRouteState = JSON.stringify(routeState);

      if (strLastRouteState !== strCurrentRouteState) {
        let indexUiState = stateFromRoute[mainIndex];
        if (strCurrentRouteState === '{}') {
          indexUiState = initialUiState[mainIndex];
        }
        if (indexUiState.virtualToggle) {
          setVirtualToggleFilters(indexUiState.virtualToggle, false);
        }
        setIndexUiState({ ...indexUiState, shouldReplace: true });
      }
    }
  );

  useEffect(() => {
    onSearchParamsChange(searchParams);
    // will be fixed once we upgrade eslint-plugin-react-hooks
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, [searchParams]);

  return null;
};
